import posthogJs from 'posthog-js';
import { API_ERROR_CODES, LEAD_HOST, SIGNUP_LOGIN_CREATED, escapeSlackText, isInternalEmail, sanitizeSignupAttribution, signupAttributionSource } from '@ruta/shared';
import { safeLocalStorage } from '@/lib/safeStorage';

export { SIGNUP_LOGIN_CREATED, LEAD_HOST, isInternalEmail };

/**
 * Step 1 of signup creates the login (Cognito) and emails the code, but the user
 * record and the company only exist after the code (step 4) — so whoever stops
 * after step 1 used to leave no trace sales could act on (RUT-4448). The moment
 * the login exists we tell PostHog who this is and send `signup_login_created`;
 * PostHog's #leads Slack destination pages sales from it.
 *
 * Who gets paged is decided by `lead_eligible`, set here: production host and a
 * prospect's email. Internal and test signups and staging still send the event
 * (so it can be checked end to end) but carry `lead_eligible: false`, which the
 * destination's filter drops.
 *
 * Hosts that send the event at all: production, and staging so the event can be
 * verified before release. Both report to the same PostHog project; staging is
 * never lead-eligible. Local and dev never send.
 */
const CAPTURE_HOSTS = new Set([LEAD_HOST, 'staging.app.getruta.com']);

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

/**
 * The PostHog id given at step 1, kept until the code is confirmed. Between the two
 * the visitor is identified but has no token, and useAuthStatus resets an
 * identified visitor with no token (a sign-out) — which on a reload mid-signup
 * would cut steps 2–4 and their replay off from the lead. It skips the reset for
 * exactly this id.
 */
const PENDING_SIGNUP_ID_KEY = 'ruta_signup_identity';
/** A signup left unfinished on a shared computer must not keep the next visitor identified as its owner. */
const PENDING_SIGNUP_TTL_MS = 24 * 60 * 60 * 1000;

export function isPendingSignupIdentity(distinctId: string | null | undefined, now = Date.now()): boolean {
  if (!distinctId) return false;
  try {
    const saved = JSON.parse(safeLocalStorage.getItem(PENDING_SIGNUP_ID_KEY) ?? 'null') as { id?: string; at?: number } | null;
    if (!saved || saved.id !== distinctId || typeof saved.at !== 'number') return false;
    if (now - saved.at > PENDING_SIGNUP_TTL_MS) {
      clearPendingSignupIdentity();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearPendingSignupIdentity(): void {
  safeLocalStorage.removeItem(PENDING_SIGNUP_ID_KEY);
}

/** The page without its query: the onboarding URL can carry `?email=`. Attribution is sent on its own. */
function pageWithoutQuery(href: string): string {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return href.split(/[?#]/)[0];
  }
}

export type SignupAttribution = Partial<Record<(typeof ATTRIBUTION_KEYS)[number], string>>;
const FIRST_TOUCH_KEY = 'ruta_signup_first_touch';

/** utm_* and Google click ids from the onboarding URL (the website forwards them). */
export function attributionFromSearch(search: string): SignupAttribution {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const out: SignupAttribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key)?.trim();
    if (value && value.length <= 200) out[key] = value;
  }
  return out;
}

/** Preserve the website handoff if signup is reloaded or resumed without its query string. */
export function firstTouchSignupAttribution(search: string): SignupAttribution {
  const current = attributionFromSearch(search);
  try {
    let saved: unknown;
    try { saved = JSON.parse(safeLocalStorage.getItem(FIRST_TOUCH_KEY) ?? 'null'); } catch { saved = null; }
    const first = sanitizeSignupAttribution(saved);
    if (first) return first;
    if (Object.keys(current).length) safeLocalStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(current));
  } catch {
    // Attribution is optional and can never block signup.
  }
  return current;
}

/** Social callbacks read this saved query: keep the first campaign and the existing identity bridge. */
export function signupAttributionHandoff(search: string): string {
  const params = new URLSearchParams(firstTouchSignupAttribution(search));
  const websiteId = new URLSearchParams(search).get('ph');
  if (websiteId) params.set('ph', websiteId);
  return params.toString();
}

/** Where they came from, in one word for the Slack line: utm_source, else the referring site, else "direct". */
export function leadSource(attribution: SignupAttribution, referrer: string): string {
  return signupAttributionSource(attribution, referrer);
}

export interface SignupLead {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  address?: string;
  /** Cognito `UserSub` from registration. Bound after the code is sent, never required to capture the lead. */
  cognitoSub?: string;
  /** PostHog distinct id from getruta.com, carried as `?ph=`. */
  websiteAnonymousId?: string;
  attribution: SignupAttribution;
  /** How the login was made (RUT-4523). Email signups leave it unset and send `email`. */
  method?: 'email' | 'google' | 'apple';
}

type LeadClient = Pick<typeof posthogJs, 'identify' | 'capture' | 'alias'> & { __loaded?: boolean };

export const SIGNUP_VALIDATION_FAILED = 'signup_validation_failed';
export const SIGNUP_REGISTRATION_FAILED = 'signup_registration_failed';
const REGISTRATION_ERROR_CODES: ReadonlySet<string> = new Set(Object.values(API_ERROR_CODES));
const LEAD_SENT_KEY = 'ruta_signup_lead_sent';

export interface SignupLeadEnv {
  hostname: string;
  href: string;
  referrer: string;
  client: LeadClient | null | undefined;
}

function browserEnv(): SignupLeadEnv {
  return {
    hostname: window.location.hostname,
    href: window.location.href,
    referrer: document.referrer,
    client: posthogJs,
  };
}

/** The event's properties. Exported for the tests and for the destination filter we document. */
export function signupLeadProperties(lead: SignupLead, env: Pick<SignupLeadEnv, 'hostname' | 'href' | 'referrer'>) {
  const email = lead.email.trim();
  const firstName = (lead.firstName ?? '').trim();
  const lastName = (lead.lastName ?? '').trim();
  const source = leadSource(lead.attribution, env.referrer);
  const leadEligible = env.hostname === LEAD_HOST && !isInternalEmail(email);
  const who = [firstName, lastName].filter(Boolean).join(' ');
  const line = escapeSlackText(
    who
      ? `New signup started: ${who} · ${email} · from ${source}`
      : `New signup started: ${email} · from ${source}`,
  );
  const page = pageWithoutQuery(env.href);
  return {
    email,
    first_name: firstName,
    last_name: lastName,
    ...lead.attribution,
    lead_source: source,
    lead_eligible: leadEligible,
    // The #leads destination posts this line as-is.
    lead_line: line,
    $current_url: page,
    page_url: page,
    app_source: 'webapp',
    // Which signup this is, in the signup draft's words (`SignupSource`): the
    // crew app's owner signup sends `crew_app`.
    signup_source: 'web',
    method: lead.method ?? 'email',
  };
}

function personTraits(lead: SignupLead, properties: ReturnType<typeof signupLeadProperties>, stage: string) {
  return {
    email: properties.email,
    ...(properties.first_name ? { first_name: properties.first_name } : {}),
    ...(properties.last_name ? { last_name: properties.last_name } : {}),
    ...(lead.company?.trim() ? { company: lead.company.trim() } : {}),
    ...(lead.phone?.trim() ? { phone: lead.phone.trim() } : {}),
    ...(lead.address?.trim() ? { address: lead.address.trim() } : {}),
    signup_stage: stage,
    signup_method: properties.method,
  };
}

function rememberPendingId(distinctId: string): void {
  safeLocalStorage.setItem(PENDING_SIGNUP_ID_KEY, JSON.stringify({ id: distinctId, at: Date.now() }));
}

function alreadySentLead(): boolean {
  return safeLocalStorage.getItem(LEAD_SENT_KEY) === '1';
}

function markLeadSent(): void {
  safeLocalStorage.setItem(LEAD_SENT_KEY, '1');
}

function readyClient(env: SignupLeadEnv): LeadClient | null {
  if (!CAPTURE_HOSTS.has(env.hostname)) return null;
  const client = env.client;
  if (!client || client.__loaded === false) return null;
  return client;
}

/** Register failures use bounded API codes, never raw messages, credentials, or query strings. */
export function recordSignupRegistrationFailed(error: unknown, attribution: SignupAttribution, env: SignupLeadEnv = browserEnv()): void {
  try {
    const client = readyClient(env);
    if (!client) return;
    const details = error && typeof error === 'object' ? error as Record<string, unknown> : {};
    const code = typeof details.code === 'string' && REGISTRATION_ERROR_CODES.has(details.code)
      ? details.code : 'registration_failed';
    const status = typeof details.status === 'number' && details.status >= 100 && details.status <= 599
      ? details.status : undefined;
    client.capture(SIGNUP_REGISTRATION_FAILED, {
      ...sanitizeSignupAttribution(attribution),
      error_reason: code,
      ...(status === undefined ? {} : { status_code: status }),
      endpoint: '/auth/register',
      method: 'email',
      app_source: 'webapp',
      $current_url: pageWithoutQuery(env.href),
      page_url: pageWithoutQuery(env.href),
    }, { send_instantly: true });
  } catch {
    // A telemetry failure must never hide the signup error or prevent retry.
  }
}

/**
 * The email is on the first screen, before the code. Identify by that email,
 * attach company / phone / address when we have them, and link the marketing
 * site's anonymous id so Lead Desk sees one person (RUT-4518).
 */
export function recordSignupEmailCaptured(lead: SignupLead, env: SignupLeadEnv = browserEnv()): void {
  try {
    const client = readyClient(env);
    if (!client) return;
    const email = lead.email.trim();
    if (!email) return;
    const properties = signupLeadProperties(lead, env);
    rememberPendingId(email);
    client.identify(email, personTraits(lead, properties, 'email_captured'));
    const websiteId = lead.websiteAnonymousId?.trim();
    if (websiteId && websiteId !== email) {
      client.alias(websiteId);
    }
    if (!alreadySentLead()) {
      client.capture(SIGNUP_LOGIN_CREATED, { ...properties, signup_stage: 'email_captured' }, { send_instantly: true });
      markLeadSent();
    }
  } catch (error) {
    console.error('[signupLead] Failed to record email capture:', error);
  }
}

/**
 * After Cognito has an unconfirmed user, alias `admin:<sub>` onto the email
 * person so the rest of the webapp keeps one identity. Does not page #leads
 * a second time.
 */
export function recordSignupLoginCreated(lead: SignupLead, env: SignupLeadEnv = browserEnv()): void {
  try {
    const client = readyClient(env);
    if (!client) return;

    const properties = signupLeadProperties(lead, env);
    const sub = lead.cognitoSub?.trim();
    const email = properties.email;
    if (email) {
      rememberPendingId(email);
      client.identify(email, personTraits(lead, properties, sub ? 'login_created' : 'email_captured'));
    }
    const websiteId = lead.websiteAnonymousId?.trim();
    if (websiteId && websiteId !== email) {
      client.alias(websiteId);
    }
    // registerUser can fall back to the email when Cognito sends no sub; never use that as an id.
    if (sub && !sub.includes('@')) {
      const distinctId = `admin:${sub}`;
      client.alias(distinctId);
      rememberPendingId(distinctId);
    }
    if (!alreadySentLead()) {
      client.capture(SIGNUP_LOGIN_CREATED, properties, { send_instantly: true });
      markLeadSent();
    }
  } catch (error) {
    console.error('[signupLead] Failed to record signup_login_created:', error);
  }
}

export function clearSignupLeadSent(): void {
  safeLocalStorage.removeItem(LEAD_SENT_KEY);
}

